import { cn } from '@v2/lib/utils';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, XIcon } from 'lucide-react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import {
  DateRange,
  DayFlag,
  DayPicker,
  Mode,
  SelectHandlerMulti,
  SelectionState,
  UI
} from 'react-day-picker';
import { useDayPicker } from 'react-day-picker';

// Add custom styles for selected weeks
import './calendar-styles.css';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Root({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const dayPicker = useDayPicker();
  const components = dayPicker.components;
  const classes = dayPicker.classNames;
  const mode = dayPicker.dayPickerProps.mode;
  const selected: Date[] = dayPicker.selected ?? [];
  const selectedSorted = useMemo(
    () =>
      mode === 'multiple'
        ? selected.sort((a, b) => a.getTime() - b.getTime())
        : [],
    [selected, mode]
  );
  const select = dayPicker.select as unknown as SelectHandlerMulti<{
    required: true;
  }>;
  const ref = React.useRef<HTMLDivElement>(null);

  return (
    <div {...props}>
      {mode === 'multiple' ? (
        <div className="flex p-0">
          <div className="flex-1" ref={ref}>
            <div className="px-4 py-2">{children}</div>
            <components.Footer className={classes[UI.Footer]} />
          </div>
          <div className="flex flex-col overflow-y-auto bg-gray-100 px-4 py-4">
            <div
              className="w-full overflow-y-auto"
              style={{
                maxHeight: ref?.current?.clientHeight
                  ? `calc(${ref.current.clientHeight}px - 50px)`
                  : 'inherit'
              }}
            >
              <ul className="min-w-[8rem] space-y-3">
                {selectedSorted.map((date: Date) => (
                  <li
                    className="flex items-center justify-between text-sm "
                    key={date.getTime().toString()}
                  >
                    <span title={format(date, 'EEE, M/d/yyyy')}>
                      {format(date, 'EEE, M/d')}
                    </span>
                    <button
                      onClick={(event) => {
                        select(date, { selected: true }, event);
                      }}
                      className="text-muted-foreground"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1">
          <div className="px-4 py-2">{children}</div>
          <components.Footer className={classes[UI.Footer]} />
        </div>
      )}
    </div>
  );
}

function cloneSelected(obj: undefined | Date[] | Date | DateRange) {
  if (obj === undefined) {
    return undefined;
  }
  if (Array.isArray(obj)) {
    return obj.map((d) => new Date(d));
  }
  if (obj instanceof Date) {
    return new Date(obj);
  }
  if ('from' in obj) {
    return {
      from: obj.from ? new Date(obj.from) : undefined,
      to: obj.to ? new Date(obj.to) : undefined
    };
  }
  return undefined;
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  onSelect,
  mode,
  weekHover = false,
  disableApplyButtons = false,
  ...props
}: CalendarProps & {
  mode: Mode;
  selected: Date[];
  disableApplyButtons?: boolean;
  weekHover?: boolean;
}) {
  const [selectedCopy, setSelectedCopy] = React.useState<Date[]>(selected);
  const [modeCopy, setModeCopy] = React.useState<Mode>(mode);

  useEffect(() => {
    setSelectedCopy(selected);
    setModeCopy(mode);
  }, [selected, mode]);

  return (
    <DayPicker
      mode={modeCopy}
      // we want Monday as first day
      weekStartsOn={1}
      //@ts-expect-error it's a copy
      selected={disableApplyButtons ? selected : selectedCopy}
      showOutsideDays={showOutsideDays}
      formatters={{
        formatWeekdayName: (date, options) => format(date, 'ccccc', options)
      }}
      className={className}
      classNames={{
        /**
         * Some styling is inside the calendar-styles.css.
         * This is done to improve smoothness of the component.
         * While small changes can be done here, don't hesitate to
         * revisit css file if something seem to visually lag.
         */
        [UI.Root]: 'border border-gray-300 rounded-lg text-black font-medium',
        [UI.Footer]: disableApplyButtons
          ? ''
          : 'mt-2 px-6 py-2 flex justify-between border-gray-300 border-t text-sm',
        [UI.Months]: 'flex relative flex-col space-y-4',
        [UI.Month]: '',
        [UI.MonthCaption]:
          'flex justify-center pt-1 absolute top-1 w-full items-center',
        [UI.CaptionLabel]: 'text-sm font-medium',
        [UI.Nav]: 'space-x-1 justify-between flex items-center relative z-10',
        [UI.PreviousMonthButton]: `${cn('h-9 w-9 bg-transparent p-2 opacity-100')}absolute left-1`,
        [UI.NextMonthButton]: `${cn('h-9 w-9 bg-transparent p-2 opacity-100')}absolute right-1`,
        [UI.MonthGrid]: 'w-full border-collapse space-y-1',
        [UI.Weekdays]: 'flex',
        [UI.Weekday]: 'text-muted-foreground w-9 font-normal text-[0.8rem]',
        [UI.Week]: cn(
          'flex w-full mt-2 relative',
          weekHover
            ? 'DEBUG_WEEK hover:rounded-full hover:bg-accent hover:text-accent-foreground'
            : ''
        ),
        [UI.Day]: cn(
          'group',
          'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
          'DAY-DEBUG text-center text-sm p-0 relative h-9 w-9 z-10'
        ),
        [SelectionState.range_start]:
          'CALENDAR-START aria-selected:bg-accent rounded-l-full aria-selected:text-accent-foreground',
        [SelectionState.range_end]:
          'CALENDAR-END aria-selected:bg-accent aria-selected:rounded-r-full aria-selected:text-accent-foreground',
        [UI.DayButton]: cn(
          'h-9 w-9',
          'hover:bg-primary hover:text-primary-foreground hover:rounded-full'
        ),
        [SelectionState.selected]:
          'bg-primary text-primary-foreground rounded-full',
        [DayFlag.today]: weekHover
          ? 'today'
          : 'today [&.today:not(.selected)]:border-primary [&.today:not(.selected)]:border-2 [&.today:not(.selected)]:rounded-full',
        [DayFlag.outside]:
          'day-outside text-muted-foreground aria-selected:text-muted-foreground',
        [DayFlag.disabled]: 'text-muted-foreground opacity-50',
        [SelectionState.range_middle]: cn(
          'CALENDAR-MIDDLE rounded-none range-middle aria-selected:bg-accent aria-selected:text-accent-foreground'
        ),
        [DayFlag.hidden]: 'invisible',
        ...classNames
      }}
      components={{
        Root,
        ...(disableApplyButtons
          ? undefined
          : {
              Footer: (props) => {
                const hasChanges =
                  JSON.stringify(selectedCopy) !== JSON.stringify(selected);
                return (
                  <div
                    className={cn(
                      props.className,
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      hasChanges ? 'h-auto opacity-100' : 'h-0 p-0 opacity-0'
                    )}
                  >
                    <button
                      className="font-bold text-muted-foreground uppercase leading-6"
                      onClick={() => {
                        setSelectedCopy(selected);
                      }}
                    >
                      Clear
                    </button>
                    <button
                      className="font-bold text-brand uppercase leading-6"
                      onClick={() => {
                        //@ts-expect-error it's a copy
                        onSelect(cloneSelected(selectedCopy));
                      }}
                    >
                      Apply
                    </button>
                  </div>
                );
              }
            }),
        Chevron: ({ className, orientation, ...props }) =>
          orientation === 'left' ? (
            <ChevronLeft className={cn('h-5 w-5', className)} {...props} />
          ) : (
            <ChevronRight className={cn('h-5 w-5', className)} {...props} />
          )
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
